import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Tooltip } from "antd"
import { XIcon } from "@heroicons/react/solid"
import { DocumentTextIcon, ExclamationCircleIcon, PencilAltIcon, PlusIcon } from "@heroicons/react/outline"
import { DialogView, Button } from "../../components"

const I18N_BASE = "replenishments.show_replenishment_boxes.supplier_invoice"
// Mirrors the backend cap (platform ReplenishmentInvoice::MAX_NUMBER_LENGTH)
export const SUPPLIER_INVOICE_MAX_LENGTH = 64

// Sticky supplier-invoice (factura) selector for CL receiving. The whole chip
// is the tap target (sized for warehouse-tablet touch); a dialog handles entry
// so the barcode scanner input never loses focus to a permanently-visible field.
export const SupplierInvoiceChip = ({ invoiceNumber, onChange, onDialogClose, valueOnly = false }) => {
  const { i18n } = useTranslation()

  const [showDialog, setShowDialog] = useState(false)
  const [draftNumber, setDraftNumber] = useState("")

  const hasInvoice = invoiceNumber !== ""

  const handleOpen = () => {
    setDraftNumber(invoiceNumber)
    setShowDialog(true)
  }

  const handleClose = () => {
    setShowDialog(false)
    onDialogClose?.()
  }

  const handleSave = () => {
    onChange(draftNumber.trim())
    handleClose()
  }

  const handleRemove = () => {
    onChange("")
    handleClose()
  }

  const LeadingIcon = hasInvoice ? DocumentTextIcon : ExclamationCircleIcon
  const TrailingIcon = hasInvoice ? PencilAltIcon : PlusIcon
  const affordanceLabel = i18n.t(`${I18N_BASE}.${hasInvoice ? "change" : "add"}`)
  const tooltip = i18n.t(`${I18N_BASE}.${hasInvoice ? "tooltip_active" : "tooltip_empty"}`)
  const chipLabel = hasInvoice
    ? (valueOnly ? invoiceNumber : i18n.t(`${I18N_BASE}.chip_label`, { number: invoiceNumber }))
    : i18n.t(`${I18N_BASE}.chip_empty`)

  // Single accent per state, from registered tokens (emerald = confirmed,
  // amber = the "missing factura" nudge). No mixed link color inside the pill.
  const chipTone = hasInvoice
    ? "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100 focus:ring-emerald-300"
    : "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100 focus:ring-amber-300"

  return (
    <>
      <Tooltip title={tooltip}>
        <button
          type="button"
          data-testid="supplier-invoice-chip"
          onClick={handleOpen}
          aria-label={`${chipLabel}, ${affordanceLabel}`}
          className={`inline-flex items-center gap-2 min-h-touch rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${chipTone}`}
        >
          <LeadingIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
          <span>{chipLabel}</span>
          <TrailingIcon className="h-4 w-4 flex-shrink-0 opacity-70" aria-hidden="true" />
        </button>
      </Tooltip>

      <DialogView setOpen={(show) => (show ? handleOpen() : handleClose())} open={showDialog}>
        <div className="w-full flex justify-between items-start">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {i18n.t(`${I18N_BASE}.dialog_title`)}
          </h3>
          <XIcon className="h-5 w-5 cursor-pointer text-gray-400 hover:text-gray-600" aria-hidden="true" onClick={handleClose} />
        </div>

        <p className="text-gray-600">{i18n.t(`${I18N_BASE}.dialog_description`)}</p>

        <label htmlFor="supplier-invoice-input" className="sr-only">
          {i18n.t(`${I18N_BASE}.dialog_title`)}
        </label>
        <input
          id="supplier-invoice-input"
          type="text"
          data-testid="supplier-invoice-input"
          autoFocus
          maxLength={SUPPLIER_INVOICE_MAX_LENGTH}
          value={draftNumber}
          onChange={(event) => setDraftNumber(event.target.value.toUpperCase())}
          onKeyDown={(event) => { if (event.key === "Enter") handleSave() }}
          placeholder={i18n.t(`${I18N_BASE}.input_placeholder`)}
          className="w-full my-2 border border-gray-300 rounded-md p-2 text-base focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />

        <div className="flex gap-3 w-full items-center">
          <Button data-testid="supplier-invoice-save" onClick={handleSave}>
            {i18n.t(`${I18N_BASE}.save`)}
          </Button>

          <Button type="secondary" data-testid="supplier-invoice-cancel" onClick={handleClose}>
            {i18n.t(`${I18N_BASE}.cancel`)}
          </Button>

          {hasInvoice && (
            <Button type="danger" dense data-testid="supplier-invoice-remove" onClick={handleRemove} className="ml-auto">
              {i18n.t(`${I18N_BASE}.remove`)}
            </Button>
          )}
        </div>
      </DialogView>
    </>
  )
}
